<script setup lang="ts">
import { useFormValidation } from '../../../../composables/useFormValidation'
import { required } from '../../../../utils/validation'
import TextInput from '../../../../components/ui/form/TextInput.vue'
import TextAreaInput from '../../../../components/ui/form/TextAreaInput.vue'
import NumberInput from '../../../../components/ui/form/NumberInput.vue'
import CheckInput from '../../../../components/ui/form/CheckInput.vue'
import DateInput from '../../../../components/ui/form/DateInput.vue'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import { api } from '../../../../api/Api'
import Toast from '../../../../services/Toast'
import { Permission } from '../../../../types/auth'
import { ref, computed, onMounted, watch } from 'vue'
import ModalFormTemplate from '../../../../components/ui/Templates/ModalFormTemplate.vue'
import { formatDate } from '../../../../utils/CommonFunctions'

const props = defineProps<{
  data?: any | null
  mode: 'create' | 'edit' | 'view' | 'showQuickView' | 'toggleStatus'
  title: string | null
  endPoint: string
  permissions?: Permission[]
  listCreateName?: string
}>()

const emit = defineEmits<{
  (e: 'save', data: any): void
  (e: 'close'): void
}>()

/**
 * Six promo types covering the standard ERP catalogue. The form is
 * type-aware — pick a type and only the fields that mean something for it
 * are shown. The evaluator on the server side dispatches on this string.
 */
const PROMO_TYPES = [
  { id: 'PCT_OFF',         name: 'Percentage off targeted items' },
  { id: 'AMOUNT_OFF',      name: 'Flat amount off per unit (targeted items)' },
  { id: 'BOGO_SAME',       name: 'Buy N get M free of same item' },
  { id: 'BOGO_CROSS',      name: 'Buy N from trigger pool, get M free from gift pool' },
  { id: 'CART_PCT_OFF',    name: 'Percentage off cart when subtotal ≥ threshold' },
  { id: 'CART_AMOUNT_OFF', name: 'Flat amount off cart when subtotal ≥ threshold' },
]

/**
 * Accounting treatment for the free units a BOGO promo gives away.
 *
 * - `discount`  : reduces the customer's bill (today's behaviour). The
 *                 cost of the free units stays in regular COGS.
 * - `free_line` : keeps the customer's bill at gross. A separate line for
 *                 the free units is added at price=0, and the cost of
 *                 those units is re-routed from COGS into a configurable
 *                 promotional-expense account.
 */
const ACCOUNTING_MODES = [
  { id: 'discount', name: 'Discount on the bill' },
  { id: 'free_line', name: 'Free item line — post cost to expense account' },
]

/**
 * `start_date` / `end_date` on the server are Eloquent `date` casts —
 * Laravel serializes those as ISO datetime ("2026-06-07T00:00:00.000000Z").
 * Native `<input type="date">` only renders strings in YYYY-MM-DD format,
 * so when editing an existing promotion the date fields appear blank.
 * Normalize once at the boundaries (initial props, handleGetRecord,
 * handleSubmit response) so the UI always sees the format it expects.
 */
const toDateInput = (v: any): string => (v ? formatDate(v) : '')

const { fields, formData, setFieldRules, validateForm, resetForm, setFormData } = useFormValidation({
  name: props.data?.name || props.listCreateName || '',
  code: props.data?.code || '',
  description: props.data?.description || '',
  type: props.data?.type || 'PCT_OFF',
  discount_type: props.data?.discount_type || 'percent',
  discount_value: props.data?.discount_value || 0,
  trigger_amount: props.data?.trigger_amount || 0,
  required_qty: props.data?.required_qty || 0,
  required_uom_id: props.data?.required_uom_id || null,
  free_qty: props.data?.free_qty || 0,
  free_uom_id: props.data?.free_uom_id || null,
  start_date: toDateInput(props.data?.start_date),
  end_date: toDateInput(props.data?.end_date),
  priority: props.data?.priority || 0,
  outlets_id: props.data?.outlets_id || null,
  customer_groups_id: props.data?.customer_groups_id || null,
  sales_channels_id: props.data?.sales_channels_id || null,
  max_uses_per_cart: props.data?.max_uses_per_cart ?? 0,
  stackable: props.data?.stackable ?? true,
  accounting_mode: props.data?.accounting_mode || 'discount',
  free_expense_account_id: props.data?.free_expense_account_id || null,
  all_products: props.data?.all_products || false,
  active: props.data?.active ?? true,
  uid: props.data?.uid || '',
  items: props.data?.items || [],
  giveaway_items: props.data?.giveaway_items || [],
})

setFieldRules('name', [required('Name is required')])
setFieldRules('type', [required('Promotion type is required')])
setFieldRules('start_date', [required('Start date is required')])

/**
 * Type-specific required-field rules. Setting these only when the promo
 * type actually uses the field keeps switching types from carrying stale
 * errors (e.g. "Discount value is required" lingering after switching from
 * PCT_OFF to BOGO_SAME). The watch below toggles them.
 */
const isPctOrAmountType = (t: string) =>
  ['PCT_OFF', 'AMOUNT_OFF', 'CART_PCT_OFF', 'CART_AMOUNT_OFF'].includes(t)
const isBogoType = (t: string) => ['BOGO_SAME', 'BOGO_CROSS'].includes(t)
const applyTypeRules = (t: string) => {
  setFieldRules(
    'discount_value',
    isPctOrAmountType(t) ? [required('Discount value is required')] : [],
  )
  setFieldRules(
    'required_qty',
    isBogoType(t) ? [required('Buy quantity is required')] : [],
  )
  setFieldRules(
    'free_qty',
    isBogoType(t) ? [required('Free quantity is required')] : [],
  )
}
applyTypeRules(fields.value.type.value)

/* ─── Per-type field visibility — keeps the form skimmable. ─── */

const showDiscountValue = computed(() =>
  ['PCT_OFF', 'AMOUNT_OFF', 'CART_PCT_OFF', 'CART_AMOUNT_OFF'].includes(fields.value.type.value),
)
const showThreshold = computed(() =>
  ['CART_PCT_OFF', 'CART_AMOUNT_OFF'].includes(fields.value.type.value),
)
const showBogoQty = computed(() =>
  ['BOGO_SAME', 'BOGO_CROSS'].includes(fields.value.type.value),
)
const showGiveaway = computed(() => fields.value.type.value === 'BOGO_CROSS')
/** Accounting mode applies to all BOGO types — the "free units" outcome is
 *  what gets routed differently. Non-BOGO types only have one path
 *  (discount), so the picker is hidden. */
const showAccounting = computed(() =>
  ['BOGO_SAME', 'BOGO_CROSS'].includes(fields.value.type.value),
)
const showFreeExpenseAccount = computed(
  () => showAccounting.value && fields.value.accounting_mode.value === 'free_line',
)
const showItemsPool = computed(() => !fields.value.all_products.value)
/**
 * CART_PCT_OFF / CART_AMOUNT_OFF apply to the cart subtotal — the trigger-
 * item pool and the "Applies to all products" toggle are conceptually
 * meaningless for them (the evaluator never reads matchedItemIds for cart-
 * level promos). Hide the whole section so admins can't get confused into
 * thinking ticking "all products" overrides the cart subtotal threshold.
 */
const showTriggerItemsSection = computed(
  () =>
    !['CART_PCT_OFF', 'CART_AMOUNT_OFF'].includes(fields.value.type.value),
)

/** When the type changes, force `discount_type` to match the type's expected
 *  shape so we don't accidentally save "amount" on a CART_PCT_OFF. Also
 *  re-apply type-specific rules so the visible fields are the ones
 *  validated. */
watch(() => fields.value.type.value, (t) => {
  if (t === 'PCT_OFF' || t === 'CART_PCT_OFF') {
    fields.value.discount_type.value = 'percent'
  } else if (t === 'AMOUNT_OFF' || t === 'CART_AMOUNT_OFF') {
    fields.value.discount_type.value = 'amount'
  }
  applyTypeRules(t)
})

/** Free-line accounting mode needs an expense account picked. */
watch(
  () => fields.value.accounting_mode.value,
  (m) => {
    setFieldRules(
      'free_expense_account_id',
      m === 'free_line'
        ? [required('Expense account is required for free-item-line mode')]
        : [],
    )
  },
  { immediate: true },
)

/* ─── Trigger-pool + giveaway-pool editing ─── */

function addItemRow() {
  formData.value.items.push({
    items_id: null,
    required_qty: null,
    required_uom_id: null,
    free_qty: null,
    free_uom_id: null,
    discount_type: null,
    discount_value: null,
  })
}
function removeItemRow(i: number) {
  formData.value.items.splice(i, 1)
}
function addGiveawayRow() {
  formData.value.giveaway_items.push({
    items_id: null,
    free_qty: 1,
    free_uom_id: null,
    discount_type: null,
    discount_value: null,
  })
}
function removeGiveawayRow(i: number) {
  formData.value.giveaway_items.splice(i, 1)
}

const applyRecord = (data: any) => {
  setFormData(data)
  // setFormData copies values verbatim — re-normalize the date casts the
  // server emits as full datetime so the <input type="date"> binds work.
  fields.value.start_date.value = toDateInput(data?.start_date)
  fields.value.end_date.value = toDateInput(data?.end_date)
}

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (!validateForm()) return
  const action = formData.value.uid != '' ? 'update' : 'create'
  const response = await api.post(
    (props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + action,
    formData.value,
  )
  if (response.success == true) {
    Toast.show('saved successfully')
    emit('save', response.data)
    if (close) emit('close')
    else if (newRecord) resetForm()
    else applyRecord(response.data)
  }
}

const handleGetRecord = async () => {
  if (!props.data) return
  const response = await api.post(
    (props.endPoint.slice(-1) == '/' ? props.endPoint : props.endPoint + '/') + 'record',
    { uid: props.data?.uid },
  )
  if (response.success == true) applyRecord(response.data)
}

/* ─── Shared option lists ────────────────────────────────────────────
 * The form can have many item / UoM pickers (trigger rows, giveaway rows,
 * header UoMs, per-item overrides). Loading each `SearchSelect` against the
 * API would fire N parallel calls every time the form opens. We fetch each
 * list ONCE on mount and pass it as `:options` to every picker below.
 */
const itemsList = ref<Array<{ id: any; name: string }>>([])
const uomsList = ref<Array<{ id: any; name: string }>>([])
const accountsList = ref<Array<{ id: any; name: string }>>([])

async function preloadLists() {
  try {
    const [items, uoms, accounts] = await Promise.all([
      api.post('inventory/item-management/items/list', {}),
      api.post('inventory/item-management/unit-of-measures/list', {}),
      api.post('finance/accounting/chart-of-accounts/list', {}),
    ])
    itemsList.value = items?.data ?? []
    uomsList.value = uoms?.data ?? []
    accountsList.value = accounts?.data ?? []
  } catch (e) {
    console.error('preloadLists failed', e)
  }
}

const isReadOnly = ref(true)
onMounted(async () => {
  isReadOnly.value = props.mode == 'view'
  await preloadLists()
  if (props.mode == 'create') {
    resetForm()
  } else if (props.data) {
    handleGetRecord()
  }
})
</script>

<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <!-- Header: name + code + active + dates -->
      <div class="grid md:grid-cols-3 gap-4">
        <TextInput
          v-model="fields.name.value"
          label="Name"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />
        <TextInput
          v-model="fields.code.value"
          label="Code (optional)"
          placeholder="SUMMER10"
          :readonly="isReadOnly"
        />
        <CheckInput v-model="fields.active.value" label="Active" :readonly="isReadOnly" />

        <SearchSelect
          v-model="fields.type.value"
          label="Promotion type"
          :options="PROMO_TYPES"
          :readonly="isReadOnly"
          :error="fields.type.touched ? fields.type.error : null"
          @blur="fields.type.touched = true"
        />
        <DateInput
          v-model="fields.start_date.value"
          label="Start date"
          :readonly="isReadOnly"
          :error="fields.start_date.touched ? fields.start_date.error : null"
          @blur="fields.start_date.touched = true"
        />
        <DateInput
          v-model="fields.end_date.value"
          label="End date"
          :readonly="isReadOnly"
          :error="fields.end_date.touched ? fields.end_date.error : null"
          @blur="fields.end_date.touched = true"
        />

        <div class="md:col-span-2">
          <NumberInput
            v-model="fields.priority.value"
            label="Priority (lower number = applied first)"
            :readonly="isReadOnly"
          />
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Only one promotion applies per sale. When several qualify on the
            same cart, the one with the lowest priority number wins.
          </p>
        </div>
        <NumberInput v-model="fields.max_uses_per_cart.value" label="Max uses per cart (0 = unlimited)" :min="0" :readonly="isReadOnly" />
        <!-- `stackable` is intentionally hidden — the evaluator now applies
             only the highest-priority qualifying promo regardless of this
             flag. The DB column stays for backward compatibility. -->
      </div>

      <TextAreaInput
        v-model="fields.description.value"
        label="Description"
        :rows="2"
        :readonly="isReadOnly"
        class="mt-4"
      />

      <!-- Targeting filters -->
      <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-2">Targeting (optional)</h3>
      <div class="grid md:grid-cols-3 gap-4">
        <SearchSelect v-model="fields.outlets_id.value" label="Outlet" end-point="system/company/outlets" :readonly="isReadOnly" />
        <SearchSelect v-model="fields.customer_groups_id.value" label="Customer group" end-point="revenue/master-data/customer-groups" :readonly="isReadOnly" />
        <SearchSelect v-model="fields.sales_channels_id.value" label="Sales channel" end-point="revenue/master-data/sales-channels" :readonly="isReadOnly" />
      </div>

      <!-- Type-specific values -->
      <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-2">Discount values</h3>
      <div class="grid md:grid-cols-3 gap-4">
        <NumberInput
          v-if="showDiscountValue"
          v-model="fields.discount_value.value"
          :label="fields.discount_type.value === 'percent' ? 'Discount percentage' : 'Discount amount (MVR)'"
          :min="0"
          :readonly="isReadOnly"
          :error="fields.discount_value.touched ? fields.discount_value.error : null"
          @blur="fields.discount_value.touched = true"
        />
        <div v-if="showThreshold" class="md:col-span-1">
          <NumberInput
            v-model="fields.trigger_amount.value"
            label="Minimum cart subtotal to qualify (MVR)"
            :min="0"
            :readonly="isReadOnly"
          />
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Promotion only fires when the cart subtotal reaches this amount.
            Set 0 for no minimum.
          </p>
        </div>
        <NumberInput
          v-if="showBogoQty"
          v-model="fields.required_qty.value"
          label="Buy quantity (N)"
          :min="1"
          :readonly="isReadOnly"
          :error="fields.required_qty.touched ? fields.required_qty.error : null"
          @blur="fields.required_qty.touched = true"
        />
        <!--
          UoM for the buy quantity. When set, the evaluator normalises both
          sides of the BOGO check to base inventory units, so a "buy 5 CTN"
          promo fires whether the cashier rang it up as 5 CTN or 60 PCS.
          Leave blank to fall back to "qty is in cart UoM" (legacy behaviour).
        -->
        <SearchSelect
          v-if="showBogoQty"
          v-model="fields.required_uom_id.value"
          label="Buy UoM (recommended)"
          :options="uomsList"
          :readonly="isReadOnly"
        />
        <NumberInput
          v-if="showBogoQty"
          v-model="fields.free_qty.value"
          label="Free quantity (M)"
          :min="1"
          :readonly="isReadOnly"
          :error="fields.free_qty.touched ? fields.free_qty.error : null"
          @blur="fields.free_qty.touched = true"
        />
        <SearchSelect
          v-if="showBogoQty"
          v-model="fields.free_uom_id.value"
          label="Free UoM (recommended)"
          :options="uomsList"
          :readonly="isReadOnly"
        />
      </div>

      <!-- Trigger pool — hidden for CART_*_OFF (cart-level promos apply
           to the cart total, not individual items, so the trigger pool +
           all_products toggle would be misleading). -->
      <div v-if="showTriggerItemsSection" class="flex items-center justify-between mt-6 mb-2">
        <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200">Trigger items</h3>
        <CheckInput v-model="fields.all_products.value" label="Applies to all products" :readonly="isReadOnly" />
      </div>
      <div v-if="showTriggerItemsSection && showItemsPool" class="space-y-2">
        <div
          v-for="(row, i) in formData.items"
          :key="i"
          class="grid gap-2 items-end p-2 border border-gray-200 dark:border-gray-700 rounded-md"
          :class="showBogoQty ? 'grid-cols-[2fr_1fr_1fr_1fr_1fr_auto]' : 'grid-cols-[2fr_auto]'"
        >
          <SearchSelect v-model="row.items_id" label="Item" :options="itemsList" :readonly="isReadOnly" />
          <NumberInput v-if="showBogoQty" v-model="row.required_qty" label="Buy N (override)" :min="0" :readonly="isReadOnly" />
          <SearchSelect
            v-if="showBogoQty"
            v-model="row.required_uom_id"
            label="Buy UoM (override)"
            :options="uomsList"
            :readonly="isReadOnly"
          />
          <NumberInput v-if="showBogoQty" v-model="row.free_qty" label="Free M (override)" :min="0" :readonly="isReadOnly" />
          <SearchSelect
            v-if="showBogoQty"
            v-model="row.free_uom_id"
            label="Free UoM (override)"
            :options="uomsList"
            :readonly="isReadOnly"
          />
          <button
            type="button"
            class="text-red-600 hover:text-red-800 px-2 py-1"
            :disabled="isReadOnly"
            @click="removeItemRow(i)"
          >Remove</button>
        </div>
        <button
          type="button"
          class="text-sm text-blue-600 hover:text-blue-800 px-2 py-1"
          :disabled="isReadOnly"
          @click="addItemRow"
        >+ Add trigger item</button>
      </div>
      <p
        v-else-if="showTriggerItemsSection"
        class="text-sm text-gray-500 italic"
      >
        Applies to every product. Disable "Applies to all products" to pick specific items.
      </p>

      <!-- Giveaway pool (BOGO_CROSS only) -->
      <div v-if="showGiveaway" class="mt-6">
        <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">Giveaway items</h3>
        <p class="text-xs text-gray-500 mb-2">
          For BOGO_CROSS, the buyer gets the configured number of free units of the cheapest
          gift item that's already in their cart from this list.
        </p>
        <div class="space-y-2">
          <div
            v-for="(row, i) in formData.giveaway_items"
            :key="i"
            class="grid grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end p-2 border border-gray-200 dark:border-gray-700 rounded-md"
          >
            <SearchSelect v-model="row.items_id" label="Gift item" :options="itemsList" :readonly="isReadOnly" />
            <NumberInput v-model="row.free_qty" label="Free qty (override)" :min="0" :readonly="isReadOnly" />
            <SearchSelect
              v-model="row.free_uom_id"
              label="Free UoM (override)"
              :options="uomsList"
              :readonly="isReadOnly"
            />
            <button
              type="button"
              class="text-red-600 hover:text-red-800 px-2 py-1"
              :disabled="isReadOnly"
              @click="removeGiveawayRow(i)"
            >Remove</button>
          </div>
          <button
            type="button"
            class="text-sm text-blue-600 hover:text-blue-800 px-2 py-1"
            :disabled="isReadOnly"
            @click="addGiveawayRow"
          >+ Add giveaway item</button>
        </div>
      </div>

      <!--
        Accounting treatment for the free units this promo gives away.
        Default is "Discount on the bill" — same outcome as before. Picking
        "Free item line" tells the SalesInvoicesController to:
          1. NOT reduce the customer's bill (post Revenue at gross),
          2. Add a separate sales_invoice_items row for the free units at
             price=0,
          3. Re-route the cost of those units from the regular COGS account
             into the configured "Free items expense account".
      -->
      <div v-if="showAccounting" class="mt-6">
        <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-2">Accounting</h3>
        <p class="text-xs text-gray-500 mb-2">
          Controls how the free units this promotion gives away are posted to
          the ledger.
        </p>
        <div class="grid md:grid-cols-2 gap-4">
          <SearchSelect
            v-model="fields.accounting_mode.value"
            label="Free item treatment"
            :options="ACCOUNTING_MODES"
            :readonly="isReadOnly"
          />
          <SearchSelect
            v-if="showFreeExpenseAccount"
            v-model="fields.free_expense_account_id.value"
            label="Free items expense account"
            :options="accountsList"
            :readonly="isReadOnly"
            :error="fields.free_expense_account_id.touched ? fields.free_expense_account_id.error : null"
            @blur="fields.free_expense_account_id.touched = true"
          />
        </div>
      </div>
    </template>
  </ModalFormTemplate>
</template>
